<script setup lang="ts">
/**
 * Terms, privacy and any other legal document, opened over the page rather
 * than navigated to — somebody halfway through a form wants to read them and
 * carry on, not lose their place.
 *
 * `slug` is a plain string: the document list is admin-managed and dynamic
 * (`GET /api/legal`), so there is no closed set of valid slugs to type against.
 *
 * The content is Quill-authored HTML from the admin panel, rendered through
 * `sanitizeHtml()` (DOMPurify) on every render and bound with `v-html`. It
 * reaches every visitor's browser regardless of who wrote it, so it is never
 * trusted raw.
 *
 * It is a game window (`GameWindow`) over a scrim: the document's title on the
 * plate, a red close button, and the text in a steel panel that scrolls
 * inside the window.
 */
import { ref, watch } from 'vue';

import { consent } from '@/consent/consent';
import { useI18n } from '@/i18n';
import { fetchLegalDoc, type LegalDoc } from '@/net/site-api';
import GameIcon from './kit/GameIcon.vue';
import GameWindow from './kit/GameWindow.vue';
import ActionButton from './panel/ActionButton.vue';
import SheetTransition from './panel/SheetTransition.vue';
import { sanitizeHtml } from './sanitize-html';

const props = defineProps<{ slug: string | null }>();
const emit = defineEmits<{ close: [] }>();
const { t } = useI18n();
/** The cookie policy also offers the consent dialog, as Tankodromo's does. Seeded by `prisma/seed.ts`. */
const COOKIE_POLICY_SLUG = 'cookie-policy';

/** The dialog is a window of its own; this one closes first so they do not stack. */
function openCookieSettings(): void {
  emit('close');
  consent.openSettings();
}
const doc = ref<LegalDoc | null>(null);
const state = ref<'loading' | 'ready' | 'missing'>('loading');

watch(
  () => props.slug,
  async (slug) => {
    doc.value = null;
    if (!slug) return;
    state.value = 'loading';
    const loaded = await fetchLegalDoc(slug);
    // The visitor may have closed it while the request was in flight.
    if (props.slug !== slug) return;
    doc.value = loaded;
    state.value = loaded ? 'ready' : 'missing';
  },
  { immediate: true },
);
</script>

<template>
  <SheetTransition>
    <GameWindow v-if="slug" width="46rem" level="dialog" aria-modal="true" :title="doc?.title || t('legal.title')" icon="letter" @close="emit('close')">
      <p v-if="state === 'loading'" class="legal-note"><GameIcon name="hourglass" :size="44" />{{ t('legal.loading') }}</p>
      <p v-else-if="state === 'missing'" class="legal-note"><GameIcon name="anchor" :size="44" />{{ t('legal.missing') }}</p>
      <div v-else class="legal-body" v-html="sanitizeHtml(doc?.content ?? '')"></div>
      <template v-if="slug === COOKIE_POLICY_SLUG" #actions>
        <ActionButton size="sm" data-test="legal-cookie-settings" @click="openCookieSettings">{{ t('consent.settingsLink') }}</ActionButton>
      </template>
    </GameWindow>
  </SheetTransition>
</template>

<style scoped>
.legal-note {
  display: grid;
  justify-items: center;
  gap: 8px;
  margin: 16px 0;
  font-family: var(--font-game-body);
  font-weight: 600;
  text-align: center;
  color: #c9d6e2;
}

.legal-body {
  font-family: var(--font-game-body);
  font-size: 0.95rem;
  line-height: 1.6;
  color: #e8eef4;
}

.legal-body :deep(p),
.legal-body :deep(li) {
  max-width: 68ch;
}

.legal-body :deep(h1),
.legal-body :deep(h2),
.legal-body :deep(h3) {
  margin: 1.2em 0 0.4em;
  font-family: var(--font-game);
  font-weight: 400;
  letter-spacing: 0.02em;
  color: var(--kit-gold);
}

.legal-body :deep(a) {
  color: var(--kit-gold);
}

.legal-body :deep(hr) {
  border: 0;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
}
</style>
